/**
 * PoliciesPage
 * 
 * Full settings page for managing dg_policies.
 * Route: /dashboard/settings/policies
 * 
 * Features:
 * - View all 5 policy types in accordion sections
 * - Edit policy content
 * - Save new versions (auto-increment)
 * - Activate specific versions
 * - View version history
 * - Quick Start wizard for new users
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import PoliciesQuickStartModal from '../components/PoliciesQuickStartModal';
import { listPolicies, createPolicy, activatePolicy, getPoliciesStatus } from '../api/policies';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  ChevronRight,
  Package,
  Zap,
  RefreshCw,
  DollarSign,
  Repeat,
  LayoutGrid,
  Sparkles,
  Save,
  CheckCircle,
  History,
  ArrowLeft
} from 'lucide-react';

const DEFAULT_BRAND_ID = 'demo-brand';

// Policy key definitions with icons and descriptions
const POLICY_KEYS = [
  {
    key: 'ship_complete',
    label: 'Ship Complete',
    description: 'Governs split vs complete shipment behavior',
    icon: Package,
    fields: [
      { name: 'default_ship_complete', label: 'Default ship complete', type: 'boolean', help: 'Wait until all items are ready' },
      { name: 'allow_split_ship', label: 'Allow split shipment', type: 'boolean', help: 'Ship items as they become available' },
      { name: 'allow_expedite_override', label: 'Allow expedite override', type: 'boolean', help: 'Override when expediting' }
    ]
  },
  {
    key: 'expedite_budget',
    label: 'Expedite Budget',
    description: 'Caps expedite approvals and routes to Slack',
    icon: Zap,
    fields: [
      { name: 'max_expedite_cost_usd', label: 'Max expedite cost (USD)', type: 'number', help: 'Maximum allowed expedite cost' },
      { name: 'requires_approval_over_usd', label: 'Approval required over (USD)', type: 'number', help: 'Routes to Slack above this' },
      { name: 'default_carrier_method', label: 'Default carrier method', type: 'string', help: 'e.g., ground, express' }
    ]
  },
  {
    key: 'refund_exceptions',
    label: 'Refund Exceptions',
    description: 'Refund override thresholds and approval routing',
    icon: DollarSign,
    fields: [
      { name: 'max_refund_without_approval_usd', label: 'Max refund without approval (USD)', type: 'number', help: 'Auto-approve up to this amount' },
      { name: 'requires_approval_over_usd', label: 'Approval required over (USD)', type: 'number', help: 'Routes to Slack above this' },
      { name: 'chargeback_risk_hold', label: 'Hold on chargeback risk', type: 'boolean', help: 'Extra review for risky refunds' }
    ]
  },
  {
    key: 'substitutions',
    label: 'Substitutions',
    description: 'Substitution allowance rules',
    icon: Repeat,
    fields: [
      { name: 'substitution_allowed_default', label: 'Substitution allowed (default)', type: 'boolean', help: 'Allow substitutes by default' },
      { name: 'substitution_requires_customer_ok', label: 'Requires customer OK', type: 'boolean', help: 'Must confirm with customer first' }
    ]
  },
  {
    key: 'channel_buffers',
    label: 'Channel Buffers',
    description: 'Store vs eCommerce inventory protection',
    icon: LayoutGrid,
    fields: [
      { name: 'protect_store_inventory', label: 'Protect store inventory', type: 'boolean', help: 'Reserve buffer for stores' },
      { name: 'buffer_pct_store', label: 'Buffer % store', type: 'number', help: 'Percentage reserved for store' },
      { name: 'buffer_pct_ecom', label: 'Buffer % eCommerce', type: 'number', help: 'Percentage reserved for online' }
    ]
  }
];

// Default content values for each policy key
const DEFAULT_CONTENT = {
  ship_complete: { default_ship_complete: false, allow_split_ship: true, allow_expedite_override: true },
  expedite_budget: { max_expedite_cost_usd: 50, requires_approval_over_usd: 25, default_carrier_method: 'ground' },
  refund_exceptions: { max_refund_without_approval_usd: 25, requires_approval_over_usd: 50, chargeback_risk_hold: true },
  substitutions: { substitution_allowed_default: false, substitution_requires_customer_ok: true },
  channel_buffers: { protect_store_inventory: true, buffer_pct_store: 10, buffer_pct_ecom: 5 }
};

function nextVersion(versions) {
  const nums = versions.map(v => parseFloat(v)).filter(n => Number.isFinite(n));
  if (!nums.length) return '1.0';
  const max = Math.max(...nums);
  const next = (Math.round((max + 0.1) * 10) / 10).toFixed(1);
  return next;
}

// Policy Section Component
function PolicySection({ policyDef, policies, forms, setFormValue, onSaveNewVersion, onActivate }) {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = policyDef.icon;
  
  const history = policies || [];
  const active = history.find(p => p.status === 'active');
  const versions = history.map(p => p.version);
  const [selectedVersion, setSelectedVersion] = useState(active?.version || '');
  
  // Ensure formContent is always an object - critical for preventing render crashes
  const defaultContent = DEFAULT_CONTENT[policyDef.key] || {};
  const formContent = (forms && forms[policyDef.key]) ? forms[policyDef.key] : defaultContent;
  
  // Safe field value getter
  const getFieldValue = (fieldName, fieldType) => {
    const value = formContent[fieldName];
    if (fieldType === 'boolean') return !!value;
    if (fieldType === 'number') return value ?? '';
    return value ?? '';
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="bg-card border-border">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">{policyDef.label}</CardTitle>
                  <CardDescription className="text-xs mt-0.5">{policyDef.description}</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={active ? 'default' : 'outline'} className={active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : ''}>
                  {active ? `v${active.version} active` : 'Not configured'}
                </Badge>
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="border-t border-border pt-4">
            {/* Version controls */}
            <div className="flex items-center gap-3 mb-4">
              {versions.length > 0 ? (
                <Select value={selectedVersion} onValueChange={setSelectedVersion}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Version..." />
                  </SelectTrigger>
                  <SelectContent>
                    {versions.map(v => (
                      <SelectItem key={v} value={v}>v{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm text-muted-foreground px-3 py-2 border border-dashed border-border rounded-md">
                  No versions yet
                </span>
              )}
              
              <Button
                size="sm"
                variant="outline"
                onClick={() => onActivate(policyDef.key, selectedVersion)}
                disabled={!selectedVersion || versions.length === 0}
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                Activate
              </Button>
              
              <Button
                size="sm"
                onClick={() => onSaveNewVersion(policyDef.key)}
                className="ml-auto"
              >
                <Save className="h-3.5 w-3.5 mr-1.5" />
                Save new version
              </Button>
            </div>
            
            {/* Form fields */}
            <div className="space-y-4">
              {policyDef.fields.map(field => (
                <div key={field.name} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                  <Label className="text-sm text-muted-foreground">{field.label}</Label>
                  <div className="md:col-span-2">
                    {field.type === 'boolean' ? (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={getFieldValue(field.name, 'boolean')}
                          onCheckedChange={(checked) => setFormValue(policyDef.key, field.name, checked)}
                        />
                        <span className="text-xs text-muted-foreground">{field.help}</span>
                      </div>
                    ) : field.type === 'number' ? (
                      <div>
                        <Input
                          type="number"
                          value={getFieldValue(field.name, 'number')}
                          onChange={(e) => setFormValue(policyDef.key, field.name, Number(e.target.value))}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">{field.help}</p>
                      </div>
                    ) : (
                      <div>
                        <Input
                          type="text"
                          value={getFieldValue(field.name, 'string')}
                          onChange={(e) => setFormValue(policyDef.key, field.name, e.target.value)}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">{field.help}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Version history */}
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                <History className="h-3.5 w-3.5" />
                Version history (latest first)
              </div>
              <div className="space-y-2">
                {history.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No versions yet</p>
                ) : (
                  history.slice(0, 5).map(p => (
                    <div key={p.id} className="flex items-center justify-between rounded-lg bg-muted/30 border border-border px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">v{p.version}</span>
                        <Badge variant="outline" className={p.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : ''}>
                          {p.status}
                        </Badge>
                      </div>
                      <span className="text-muted-foreground">
                        {p.updated_at ? new Date(p.updated_at).toLocaleString() : '—'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default function PoliciesPage() {
  const navigate = useNavigate();
  const brandId = DEFAULT_BRAND_ID;
  
  const [policies, setPolicies] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuickStart, setShowQuickStart] = useState(false);
  
  // Form state for each policy key
  const [forms, setForms] = useState(() => ({ ...DEFAULT_CONTENT }));

  // Load policies
  const refresh = async () => {
    setLoading(true);
    try {
      const [policiesRes, statusRes] = await Promise.all([
        listPolicies(brandId),
        getPoliciesStatus(brandId)
      ]);
      
      const items = policiesRes.policies || [];
      setPolicies(items);
      setStatus(statusRes);
      
      // Load active content into forms
      const nextForms = { ...DEFAULT_CONTENT };
      for (const p of items) {
        if (p.status === 'active') {
          nextForms[p.policy_key] = p.content || nextForms[p.policy_key];
        }
      }
      setForms(nextForms);
    } catch (err) {
      console.error('Failed to load policies:', err);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Group policies by key
  const grouped = useMemo(() => {
    const map = {};
    for (const p of policies) {
      map[p.policy_key] = map[p.policy_key] || [];
      map[p.policy_key].push(p);
    }
    // Sort each by version desc
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (parseFloat(b.version) || 0) - (parseFloat(a.version) || 0));
    }
    return map;
  }, [policies]);

  // Update form value
  const setFormValue = (policyKey, field, value) => {
    setForms(prev => ({
      ...prev,
      [policyKey]: { ...(prev[policyKey] || {}), [field]: value }
    }));
  };

  // Save new version
  const handleSaveNewVersion = async (policyKey) => {
    try {
      const versions = (grouped[policyKey] || []).map(p => p.version);
      const version = nextVersion(versions);
      
      await createPolicy({
        brand_id: brandId,
        policy_key: policyKey,
        version,
        status: 'active',
        content: forms[policyKey] || {}
      });
      
      toast.success(`Saved ${policyKey} v${version}`, {
        description: 'New version is now active'
      });
      
      await refresh();
    } catch (err) {
      console.error('Failed to save policy:', err);
      toast.error('Failed to save policy', { description: err.message });
    }
  };

  // Activate version
  const handleActivate = async (policyKey, version) => {
    if (!version) return;
    
    try {
      await activatePolicy(brandId, policyKey, version);
      toast.success(`Activated ${policyKey} v${version}`);
      await refresh();
    } catch (err) {
      console.error('Failed to activate policy:', err);
      toast.error('Failed to activate policy', { description: err.message });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MerchantHeader />
      
      <main className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard/settings')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Settings
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Policies</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Configure thresholds for Decision Pack and Slack approvals. These settings help enforce what's allowed.
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setShowQuickStart(true)}>
              <Sparkles className="h-4 w-4 mr-2" />
              Quick Start
            </Button>
          </div>
        </div>

        {/* Status summary */}
        {status && (
          <Card className="bg-card border-border mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${status.configured ? 'bg-emerald-500/10' : 'bg-amber-400/10'}`}>
                    {status.configured ? (
                      <CheckCircle className="h-5 w-5 text-emerald-400" />
                    ) : (
                      <Sparkles className="h-5 w-5 text-amber-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">
                      {status.configured 
                        ? `Policies configured (${status.active_count}/${status.total_supported})`
                        : 'Policies not configured yet'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {status.configured 
                        ? 'Decision Pack and Slack approvals are policy-aware'
                        : 'Use Quick Start to configure in 2 minutes'}
                    </p>
                  </div>
                </div>
                
                {status.missing_keys?.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Missing: {status.missing_keys.join(', ')}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Policy sections */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="bg-card border-border">
                <CardContent className="p-6">
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {POLICY_KEYS.map(policyDef => (
              <PolicySection
                key={policyDef.key}
                policyDef={policyDef}
                policies={grouped[policyDef.key] || []}
                forms={forms}
                setFormValue={setFormValue}
                onSaveNewVersion={handleSaveNewVersion}
                onActivate={handleActivate}
              />
            ))}
          </div>
        )}

        {/* Policy FAQ link */}
        <Card className="bg-muted/30 border-border mt-8">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              <strong>What are policies?</strong> Policies define what's allowed in common exception scenarios 
              (ship complete vs split, expedite caps, refund thresholds). They make Decision Pack and Slack 
              approvals consistent and auditable.{' '}
              <a href="/docs/policies" className="text-primary hover:underline">Learn more →</a>
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Quick Start Modal */}
      <PoliciesQuickStartModal
        open={showQuickStart}
        onClose={() => setShowQuickStart(false)}
        brandId={brandId}
        onDone={refresh}
      />
    </div>
  );
}
